<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { marketForex, topCrypto, topStocks, marketCommodities, topIndices } from "../helpers/live-market";
import { normalizeDiscoveryItems } from "../helpers/markets";
import { useCustomFetch } from "../composables/useCustomFetch";

interface Props {
  marketType: string;
}

interface TickInfo {
  Ask: number;
  Bid: number;
  error?: string;
}

interface Stock {
  name: string;
  symbol: string;
  mt5Name?: string;
  mt5Symbol?: string;
  image?: string;
  trendingImage?: string;
  currency?: string;
  value?: string | number;
  percent?: string | number;
}

interface ApiResponse {
  tick_data: Record<string, TickInfo>;
}

interface DisplayItem {
  title: string;
  image?: string;
  price: string;
  change: string;
  colorClass: string;
}

const props = defineProps<Props>();

// Get correct market data based on type
const marketData = computed(() => {
  const data = (() => {
    switch (props.marketType.toLowerCase()) {
      case "forex":
        return marketForex.slice(0, 4);
      case "crypto":
        return topCrypto;
      case "stocks":
        return topStocks;
      case "commodities":
        return marketCommodities.filter((_, index) => index % 2 === 0).slice(0, 4);
      case "indices":
        return topIndices;
      default:
        return [];
    }
  })();
  
  // console.log(`[MarketItems] ${props.marketType} data:`, data);
  // console.log(`[MarketItems] ${props.marketType} symbols:`, data.map(item => item.mt5Symbol));
  return data;
});

// Get the right parameter for normalizeDiscoveryItems based on market type
const getNameProp = () => {
  const prop = props.marketType.toLowerCase() === "stocks" ? "name" : "mt5Name";
  // console.log(`[MarketItems] Using name prop for normalization: ${prop}`);
  return prop;
};

// Create the symbols array for the request
const symbolsToRequest = computed(() => 
  marketData.value.map(item => item.mt5Symbol)
);

// Create a ref to trigger refetching
const refreshTrigger = ref(0);

// Fetch data with refetching capability
const { data: tickData, status, refresh } = useCustomFetch("/api/mt5/ticks", {
  method: "POST",
  body: computed(() => ({
    symbols: symbolsToRequest.value,
  })),
  key: computed(() => `market-items-${props.marketType}-${refreshTrigger.value}`).value,
});

// Watch for market type changes to refresh the data
watch(() => props.marketType, (newType, oldType) => {
  // console.log(`[MarketItems] Market type changed from ${oldType} to ${newType}, refreshing data`);
  refreshTrigger.value++; // This will trigger a refetch with the new symbols
}, { immediate: true });

// Watch for tick data changes
watch(() => tickData.value, (newData) => {
  // console.log(`[MarketItems] ${props.marketType} API response:`, newData);
  
  if ((newData as any)?.tick_data) {
    const responseSymbols = Object.keys((newData as any).tick_data);
    // console.log(`[MarketItems] ${props.marketType} tick_data keys:`, responseSymbols);
    // console.log(`[MarketItems] Requested symbols:`, symbolsToRequest.value);
    
    // Check for matching symbols
    marketData.value.forEach(item => {
      const symbol = item.mt5Symbol;
      const exists = (newData as any).tick_data[symbol] !== undefined;
      // console.log(`[MarketItems] Symbol ${symbol} exists in API response: ${exists}`);
      
      if (exists) {
        // console.log(`[MarketItems] Data for ${symbol}:`, (newData as any).tick_data[symbol]);
      }
    });
  } else {
    // console.log(`[MarketItems] No tick_data available for ${props.marketType}`);
  }
}, { immediate: true });

// Map processed items following the exact same pattern as individual pages
const items = computed(() => {
  // console.log(`[MarketItems] Computing items for ${props.marketType}, tickData:`, tickData.value);
  
  if (!tickData.value || !(tickData.value as any)?.tick_data) {
    // console.log(`[MarketItems] No tick data available for ${props.marketType}, returning empty array`);
    return [];
  }

  const normalized = normalizeDiscoveryItems(
    marketData.value, 
    (tickData.value as any).tick_data,
    getNameProp()
  );
  
  // console.log(`[MarketItems] Normalized ${props.marketType} items:`, normalized);
  return normalized;
});

// Helper function for determining color class
function getColorClass(item) {
  if (!item.change) return 'blue';
  
  const changeText = item.change.replace('%', '').replace('+', '');
  const value = parseFloat(changeText);
  
  if (isNaN(value)) return 'blue';
  if (value > 0) return 'green';
  if (value < 0) return 'red';
  return 'blue';
}

// Log the mounted market type for debugging
onMounted(() => {
  // console.log(`[MarketItems] Component mounted for market type: ${props.marketType}`);
});
</script>

<template>
  <FullScreenLoader v-if="status === 'pending'" />
  <div v-else class="row">
    <div v-for="(item, index) in items" :key="index" class="col-md-3">
      <div class="itemstock">
        <div
          :class="[getColorClass(item), 'colorize']"
          data-aos="fade-up"
          data-aos-anchor-placement="top-bottom"
          :data-aos-delay="300 + index * 300"
        ></div>
        <div class="glassblock">
          <img :src="item.image" :alt="item.name" height="40" />
          <h2>{{ item.name }}</h2>
          <hr />
          <span class="price">{{ item.value }}</span>
          <NuxtLinkLocale href="/register" class="trade-btn">Trade</NuxtLinkLocale>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.itemstock {
  position: relative;
  margin-bottom: 20px;
}

.colorize {
  width: 40px;
  border-radius: 50%;
  margin: 0 auto;
  position: relative;
  z-index: 0;
  top: 150px;
  opacity: 0.07;
}

.colorize.red {
  background-color: #d20006;
  box-shadow: 0 0 60px 30px #d20006, 0 0 100px 60px #d20006, 0 0 140px 90px #d20006;
}

.colorize.green {
  background-color: #02ca55;
  box-shadow: 0 0 60px 30px #02ca55, 0 0 100px 60px #02ca55, 0 0 140px 90px #02ca55;
}

.colorize.blue {
  background-color: #1387e1;
  box-shadow: 0 0 60px 30px #1387e1, 0 0 100px 60px #1387e1, 0 0 140px 90px #1387e1;
}

.itemstock:hover .colorize {
  opacity: 0.12;
  top: 85px;
  transition: all 0.5s ease;
}

.glassblock {
  border: 1px solid #e6e6e6;
  border-radius: 30px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.2);
  position: relative;
  z-index: 99;
  backdrop-filter: blur(60px);
  padding: 10px 20px;
}

.glassblock img {
  float: left;
}

.glassblock h2 {
  float: left;
  font-size: 18px;
  margin-top: 5px;
  margin-left: 10px;
  margin-bottom: 5px;
}

.glassblock .trade-btn {
  float: right;
  border: 1px solid #e3e2e2;
  text-decoration: none;
  border-radius: 50px;
  padding: 5px 12px;
  font-weight: 600;
  color: #000;
}

.glassblock .trade-btn:hover {
  box-shadow: 10px 0px 30px 0px rgba(0, 0, 0, 0.12);
}

.glassblock hr {
  display: block;
  clear: both;
  height: 5px;
  background: none;
  border: 1px solid #ececec;
  margin-top: 0px;
  margin-bottom: 5px;
}

.price {
  font-weight: 600;
  font-size: 1.1rem;
  display: block;
}
</style>
